import React, { useState } from "react";
import { useSubmitForm } from "core/hooks/submitFormHook";
import Joi from "joi";
import { EditTextField } from "core/components/inputWithAccessControl";
import { toast } from "react-toastify";
import { useLoading } from "core/hooks/loadingHook";
import { CapitalizedText } from "core/components/capitalizedText";
import biosphereBg from "../assets/img/biosphere_bg.jpg";

import {
  login,
  saveToken,
  saveRefreshToken,
  saveUser,
} from "features/authentication/authService";

import * as jwt_decode from "jwt-decode";
import _ from "lodash";
import roles from "roles.json";
import { useNavigate } from "react-router-dom";

export const Login = (props) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const schema = Joi.object({
    username: Joi.string().label("Login Id").trim().min(3).max(30).required(),
    password: Joi.string().label("Password").trim().min(3).max(30).required(),
  });

  const loginCallback = async () => {
    startLoading("Checking credentials....");
    try {
      const { username, password } = inputs;

      const response = await login(username, password);
      saveToken(response.data.accessToken);
      saveRefreshToken(response.data.refreshToken);
      const decodedToken = jwt_decode(response.data.accessToken);
      const role = decodedToken.role;
      const passwordExpiry = decodedToken.passwordExpiry;
      const expirationDate = new Date(passwordExpiry);
      const currentDate = new Date();
      const remainingDays = Math.ceil(
        (expirationDate - currentDate) / (1000 * 60 * 60 * 24)
      );
      const autoExpiry = decodedToken.autoExpiry;

      saveUser(JSON.stringify(response.data));
      toast.success("Logged In Successfully");
      stopLoading();

      if (autoExpiry == "Y" && expirationDate < currentDate) {
        localStorage.setItem("resetPasswordToken", password);
        toast.warning("Your password has expired. Please reset your password.");
        navigate("/company/resetPassword");
      } else if (_.includes(role, roles.COMPANY_ROLE)) {
        navigate("/company");
      } else if (_.includes(role, roles.CPCB_ROLE)) {
        navigate("/cpcb");
      }

      if (
        window.location.pathname !== "/company/resetPassword" &&
        autoExpiry == "Y"
      ) {
        if (remainingDays < 5) {
          toast.warning(
            `Your password will expire in ${remainingDays} days. Please reset your password!`
          );
        }
      }
    } catch (error) {
      stopLoading();
      if (
        error.response &&
        error.response.status === 401 &&
        error.response.data &&
        error.response.data.message
      ) {
        toast.error(<CapitalizedText message={error.response.data.message} />);
      } else {
        toast.error(<CapitalizedText message={"Cannot connect to server"} />);
      }
    }
  };

  const { inputs, errors, handleInputChange, handleSubmit } = useSubmitForm(
    schema,
    loginCallback
  );
  const { loaderContainer, startLoading, stopLoading } = useLoading();

  const featureBadges = [
    "Compliance Visibility",
    "Audit Readiness",
    "Real-Time Monitoring",
  ];

  const securityItems = [
    { icon: "lock", label: "Secure Access" },
    { icon: "admin_panel_settings", label: "Role-Based Permissions" },
    { icon: "enhanced_encryption", label: "Encrypted Data Transmission" },
  ];

  return (
    <React.Fragment>
      <div className="bio__page">

        {/* ── Main ── */}
        <main className="bio__main">

          {/* VAA logo — top right */}
          <div className="bio__logo-corner">
            <img
              className="bio__logo-img"
              src={`${process.env.PUBLIC_URL}/vaa_favicon.ico`}
              alt="VAA Technologies Logo"
            />
          </div>

          {/* ════ LEFT PANEL ════ */}
          <section className="bio__left-panel">
            {/* Background image */}
            <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
              <img
                src={biosphereBg}
                alt="Environmental monitoring facility"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              {/* Gradient overlay — dark at bottom, transparent at top */}
              <div style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(to top, rgba(33,49,69,0.95) 0%, rgba(33,49,69,0.55) 50%, transparent 100%)",
              }} />
            </div>

            {/* Bottom content */}
            <div style={{
              position: "relative",
              zIndex: 10,
              padding: "0 40px 56px",
              maxWidth: 480,
            }}>
              <h1 style={{
                color: "#fff",
                fontSize: "36px",
                fontWeight: 700,
                lineHeight: 1.2,
                marginBottom: "16px",
                letterSpacing: "-0.02em",
              }}>
                Environmental Compliance<br />Made Simple
              </h1>
              <p style={{
                color: "#dce9ff",
                fontSize: "18px",
                lineHeight: 1.75,
                marginBottom: "28px",
              }}>
                Monitor compliance, manage risks and stay audit-ready across all your facilities.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                {featureBadges.map((label) => (
                  <div key={label} style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "12px",
                    background: "rgba(255,255,255,0.1)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    borderRadius: "8px",
                    padding: "12px 16px",
                    width: "fit-content",
                  }}>
                    <span
                      className="material-symbols-outlined"
                      style={{
                        fontSize: 16,
                        color: "#67e8f9",
                        fontVariationSettings: "'FILL' 1",
                      }}
                    >
                      check_circle
                    </span>
                    <span style={{ color: "#fff", fontSize: "14px", fontWeight: 500 }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ════ RIGHT PANEL ════ */}
          <section className="bio__right-panel">

            {/* Go To Compliance Command Center */}
            {/* <div className="bio__cta-wrap" style={{ marginBottom: "16px", textAlign: "center" }}>
              <a
                href="#"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  backgroundColor: "#e0f2f4",
                  border: "1px solid #c3c5d9",
                  borderRadius: "4px",
                  padding: "9.6px 16px",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#0e7490",
                  textDecoration: "none",
                }}
              >
                Go To Compliance Command Center
                <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                  arrow_forward
                </span>
              </a>
            </div> */}

            {/* Card */}
            <div className="bio__card" style={{
              width: "100%",
              maxWidth: 440,
              backgroundColor: "#fff",
              borderRadius: "12px",
              border: "1px solid #c3c5d9",
              padding: "28px",
              boxShadow:
                "0px 20px 25px -5px rgba(0,0,0,0.05), 0px 8px 10px -6px rgba(0,0,0,0.02)",
            }}>

              {/* Header */}
              <div className="bio__card-header" style={{ textAlign: "center", marginBottom: "16px" }}>
                <h2 className="bio__card-title" style={{
                  fontSize: "36px",
                  fontWeight: 700,
                  color: "#0e7490",
                  marginBottom: "4px",
                  letterSpacing: "-0.02em",
                  textShadow: "0 0 15px rgba(14,116,144,0.2)",
                  fontFamily: "'Inter', sans-serif",
                }}>
                  ReconBiosphere
                </h2>
                <p style={{ fontSize: "16px", color: "#565e74", margin: 0 }}>
                  Environmental Compliance Platform
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit}>

                <div className="login__field">
                  <EditTextField
                    identifier="username"
                    labelName="User ID"
                    handleInputChange={handleInputChange}
                    inputs={inputs}
                    errors={errors}
                    editAccess={true}
                    required={false}
                  />
                </div>

                <div className="login__field">
                  <EditTextField
                    identifier="password"
                    labelName="Password"
                    type={showPassword ? "text" : "password"}
                    handleInputChange={handleInputChange}
                    inputs={inputs}
                    errors={errors}
                    editAccess={true}
                    required={false}
                  />
                </div>

                {/* Show Password + Forgot Password */}
                <div style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                  marginTop: "4px",
                }}>
                  <label style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    color: "#434656",
                    userSelect: "none",
                  }}>
                    <input
                      type="checkbox"
                      checked={showPassword}
                      onChange={(e) => setShowPassword(e.target.checked)}
                      style={{ accentColor: "#0e7490", width: 14, height: 14 }}
                    />
                    Show Password
                  </label>
                  {/* <a
                    href="/company/resetPassword"
                    style={{
                      fontSize: "14px",
                      color: "#0e7490",
                      textDecoration: "none",
                    }}
                  >
                    Forgot Password?
                  </a> */}
                </div>

                {/* Sign In button */}
                <button
                  type="submit"
                  style={{
                    width: "100%",
                    backgroundColor: "#0e7490",
                    color: "#fff",
                    fontSize: "14px",
                    fontWeight: 500,
                    padding: "12px 16px",
                    borderRadius: "8px",
                    border: "none",
                    cursor: "pointer",
                    letterSpacing: "0.02em",
                    transition: "opacity 0.15s",
                    fontFamily: "'Inter', sans-serif",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                >
                  Sign In
                </button>
              </form>

              {/* Contact Administrator */}
              {/* <div style={{ marginTop: "24px", textAlign: "center" }}>
                <a
                  href="#"
                  style={{ fontSize: "14px", color: "#565e74", textDecoration: "none" }}
                >
                  Contact Administrator
                </a>
              </div> */}

              {/* Security Indicators */}
              <div className="bio__security" style={{
                marginTop: "16px",
                paddingTop: "16px",
                borderTop: "1px solid #c3c5d9",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}>
                {securityItems.map(({ icon, label }) => (
                  <div
                    key={label}
                    style={{ display: "flex", alignItems: "center", gap: "8px", color: "#565e74" }}
                  >
                    <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
                      {icon}
                    </span>
                    <span style={{
                      fontSize: "12px",
                      fontWeight: 600,
                      letterSpacing: "0.05em",
                    }}>
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </main>

        {/* ── Footer ── */}
        <footer className="bio__footer">
          <div style={{
            maxWidth: 1280,
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "16px",
          }}>
            <span style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#0b1c30",
              fontFamily: "'Inter', sans-serif",
            }}>
              BIOSPHERE
            </span>

            {/* <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: "16px" }}>
              {["Privacy Policy", "Terms of Service", "Support", "Version 1.0"].map((item) => (
                <a
                  key={item}
                  href="#"
                  style={{ fontSize: "14px", color: "#565e74", textDecoration: "none" }}
                >
                  {item}
                </a>
              ))}
            </div> */}

            <span style={{ fontSize: "14px", color: "#565e74" }}>
              © 2016 VAA Technologies Pvt Ltd
            </span>
          </div>
        </footer>
      </div>

      {loaderContainer}
    </React.Fragment>
  );
};
